/**
 * G-72 — the web side of the screenshot, and the ONLY file that knows the plugin.
 *
 * The native half is `android/app/src/main/java/cc/flowerworld/kiosk/ScreenshotPlugin.java`.
 * Same seam rule as NFC and dictation: features ask "take a picture of this
 * screen", never `window.Capacitor`.
 *
 * ABSENT, NEVER BROKEN. In a plain browser there is no plugin, so `capture()`
 * returns null and the report simply goes without a picture. The message and
 * the console tail are what make a report answerable; the image is the bonus.
 * A bug reporter that refused to file because it could not screenshot would
 * fail exactly when someone was trying to tell us something.
 */

import { isNativeShell } from "./shell";

interface KioskScreenshotPlugin {
  capture(): Promise<{ base64: string; width: number; height: number }>;
}

function plugin(): KioskScreenshotPlugin | undefined {
  if (typeof window === "undefined") return undefined;
  const capacitor = (
    window as Window & { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  return capacitor?.Plugins?.KioskScreenshot as KioskScreenshotPlugin | undefined;
}

export function screenshotSupported(): boolean {
  return isNativeShell() && plugin() !== undefined;
}

/**
 * Turn the native base64 into a file the multipart upload can carry.
 *
 * Exported and pure so the decoding is provable without a tablet — a silently
 * corrupt image would otherwise only show up as an unopenable file in a report
 * weeks later.
 */
export function pngFromBase64(base64: string, name = "screen.png"): File {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type: "image/png" });
}

/**
 * A picture of the current screen, or null if this device cannot take one.
 *
 * Never throws. Callers are usually already handling a failure when they ask.
 */
export async function captureScreenshot(): Promise<File | null> {
  const p = plugin();
  if (!isNativeShell() || !p) return null;
  try {
    const shot = await p.capture();
    if (!shot?.base64) return null;
    return pngFromBase64(shot.base64);
  } catch {
    return null;
  }
}
